export const blogPosts = [
  {
    id: 1,
    slug: 'how-to-prepare-for-your-driving-test',
    title: 'How to Prepare for Your UK Driving Test in 2025',
    excerpt: 'Essential tips and advice for passing your practical driving test on the first attempt.',
    content: `Preparing for your driving test can be nerve-wracking, but with the right approach, you can increase your chances of success significantly. Here are our top tips for 2025.

## Practice Makes Perfect

The more you practice, the more confident you'll feel on test day. Aim for at least 40-50 hours of practice before your test, including various road conditions and weather.

## Know Your Maneuvers

Make sure you're comfortable with all the key maneuvers:
- Parallel parking
- Bay parking (forward and reverse)
- Pulling up on the right
- Emergency stop

## Study the Highway Code

The Highway Code is your bible for road rules. Make sure you understand:
- Road signs and markings
- Right of way rules
- Speed limits
- Pedestrian crossings

## Book Your Test Strategically

Choose a time when you'll be most relaxed and traffic will be lighter. Early morning or mid-afternoon slots are often less busy.

## Get Enough Sleep

A good night's sleep before your test is crucial. You need to be alert and focused.

Good luck with your test!`,
    category: 'Test Preparation',
    tags: ['driving test', 'preparation', 'tips', 'UK driving'],
    author: 'TESTBOOK Team',
    date: '2025-01-10',
    image: 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=800&q=80'
  },
  {
    id: 2,
    slug: 'common-driving-test-mistakes-to-avoid',
    title: 'Top 10 Common Driving Test Mistakes to Avoid',
    excerpt: 'Learn about the most frequent errors that lead to test failures and how to avoid them.',
    content: `Understanding common mistakes can help you avoid them on test day. Here are the top 10 errors candidates make.

## 1. Not Checking Mirrors Enough

This is the most common mistake. Check your mirrors regularly, especially before:
- Moving off
- Changing lanes
- Turning
- Stopping

## 2. Poor Observation at Junctions

Take your time at junctions. Look right, left, and right again before proceeding.

## 3. Incorrect Use of Signals

Signal early and cancel signals promptly. Don't confuse other road users.

## 4. Not Following Road Signs

Pay attention to all road signs and markings. Missing a speed limit sign can result in failure.

## 5. Hesitation

Being too cautious can be as dangerous as being reckless. Drive confidently when safe to do so.

## 6. Steering Issues

Keep both hands on the wheel and maintain proper control at all times.

## 7. Poor Speed Control

Drive at appropriate speeds for conditions. Not too fast, not too slow.

## 8. Parking Errors

Practice your parking maneuvers until they're second nature.

## 9. Not Understanding Road Markings

Know what different road markings mean and follow them.

## 10. Failing Independent Driving

Stay calm during the independent driving section. It's okay to make a wrong turn - just drive safely.

Avoid these mistakes and you'll be well on your way to passing!`,
    category: 'Test Preparation',
    tags: ['driving test', 'mistakes', 'tips', 'common errors'],
    author: 'TESTBOOK Team',
    date: '2025-01-08',
    image: 'https://images.unsplash.com/photo-1502489597346-dad15683d4c2?w=800&q=80'
  },
  {
    id: 3,
    slug: 'understanding-uk-driving-test-changes-2025',
    title: 'UK Driving Test Changes in 2025: What You Need to Know',
    excerpt: 'Stay updated with the latest changes to the UK driving test format and requirements.',
    content: `The DVSA has introduced several updates to the driving test in 2025. Here's what's changed.

## Extended Test Duration

The test now lasts approximately 40 minutes, up from 38 minutes previously.

## Increased Independent Driving

Independent driving now accounts for 25 minutes of the test, up from 20 minutes.

## Sat Nav Usage

Most tests (4 out of 5) now include following directions from a sat nav instead of road signs.

## New Maneuvers

The 'pulling up on the right' maneuver has been added to the list of possible test maneuvers.

## Show Me, Tell Me Questions

One 'show me' question is now asked while driving, rather than both questions being asked at the start.

## Pass Rate Statistics

The current pass rate is around 47%, so preparation is key.

## What Hasn't Changed

- The test still covers the same core skills
- You still need to demonstrate safe, confident driving
- The Highway Code remains fundamental

Make sure you're prepared for these changes!`,
    category: 'News & Updates',
    tags: ['driving test', '2025', 'changes', 'DVSA', 'updates'],
    author: 'TESTBOOK Team',
    date: '2025-01-05',
    image: 'https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?w=800&q=80'
  },
  {
    id: 4,
    slug: 'choosing-the-right-driving-instructor',
    title: 'How to Choose the Right Driving Instructor in the UK',
    excerpt: 'Finding a good driving instructor can make all the difference to your learning experience.',
    content: `Choosing the right driving instructor is crucial to your success. Here's what to look for.

## Check Qualifications

Your instructor must be:
- Approved by the DVSA
- Display a green badge (fully qualified) in their windscreen
- Have valid insurance for teaching

## Look for Experience

Experienced instructors have seen it all and know how to handle different learning styles.

## Ask About Pass Rates

Good instructors should be happy to share their pass rates with you.

## Check Reviews

Look for reviews online from previous students. Social media and Google reviews are helpful.

## Trial Lesson

Many instructors offer a discounted first lesson. Use this to see if you're comfortable with their teaching style.

## Communication Style

Your instructor should be:
- Patient
- Clear in explanations
- Encouraging
- Professional

## Vehicle Condition

The car should be:
- Clean and well-maintained
- Have dual controls
- Comfortable to drive

## Flexibility

Can they accommodate your schedule? Are they reliable with timings?

## Pricing

While cost matters, the cheapest isn't always the best. Consider value for money.

Take your time finding the right instructor - it's worth it!`,
    category: 'Learning to Drive',
    tags: ['driving instructor', 'learning', 'tips', 'DVSA'],
    author: 'TESTBOOK Team',
    date: '2025-01-03',
    image: 'https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?w=800&q=80'
  },
  {
    id: 5,
    slug: 'understanding-driving-test-centres-uk',
    title: 'Guide to UK Driving Test Centres: What to Expect',
    excerpt: 'Learn what happens at a driving test centre and how to prepare for your visit.',
    content: `Knowing what to expect at your test centre can help reduce anxiety. Here's a complete guide.

## Before You Arrive

Make sure you have:
- Your provisional licence (photocard)
- Your theory test pass certificate
- Arrived 10 minutes early

## At the Test Centre

### Check-In Process

You'll need to:
1. Report to reception
2. Show your documents
3. Wait in the waiting area
4. Meet your examiner

### The Eyesight Test

You'll be asked to read a number plate from 20 meters away. Practice this beforehand!

### Vehicle Safety Questions

The examiner will ask 'tell me' and 'show me' questions about vehicle safety.

## During the Test

Your examiner will:
- Give clear directions
- Not try to trick you
- Mark faults on their tablet
- Remain professional and neutral

## Popular Test Centres

- London (Wood Green)
- Manchester
- Birmingham
- Leeds
- Bristol

## After the Test

You'll receive:
- Immediate results
- A feedback sheet
- Your pass certificate (if successful)

## Tips for Success

- Treat your instructor's car like your own
- Drive naturally
- Don't worry about minor mistakes
- Stay calm

Remember, examiners want you to pass - they're assessing if you're safe to drive alone.`,
    category: 'Test Preparation',
    tags: ['test centre', 'preparation', 'what to expect', 'UK'],
    author: 'TESTBOOK Team',
    date: '2024-12-28',
    image: 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=800&q=80'
  },
  {
    id: 6,
    slug: 'driving-in-different-weather-conditions',
    title: 'Mastering Driving in Different Weather Conditions',
    excerpt: 'Essential tips for driving safely in rain, fog, snow, and other challenging weather.',
    content: `British weather is unpredictable, so you need to be prepared for all conditions.

## Driving in Rain

### Key Points:
- Reduce speed by at least one-third
- Increase following distance to 4 seconds
- Use dipped headlights
- Watch for aquaplaning
- Test brakes after going through water

## Driving in Fog

### Safety Tips:
- Use fog lights (front and rear)
- Reduce speed significantly
- Use windscreen wipers and demister
- Don't follow tail lights too closely
- Listen for traffic you can't see

## Driving in Snow and Ice

### Essential Advice:
- Use highest gear possible
- Brake gently and early
- Increase stopping distances by 10x
- Clear all windows before setting off
- Carry emergency supplies

## Driving in Strong Wind

### Be Aware:
- High-sided vehicles are vulnerable
- Grip steering wheel firmly
- Be cautious on exposed roads
- Watch for debris
- Give cyclists extra space

## Driving in Sun Glare

### Protection:
- Use sun visor
- Wear sunglasses
- Keep windscreen clean
- Increase following distance
- Be prepared to stop

## Night Driving

### Best Practices:
- Use dipped headlights in built-up areas
- Use full beam on unlit roads (when safe)
- Watch your speed
- Be extra cautious
- Stay alert for pedestrians

Always adjust your driving to the conditions!`,
    category: 'Driving Skills',
    tags: ['weather', 'safety', 'driving conditions', 'tips'],
    author: 'TESTBOOK Team',
    date: '2024-12-25',
    image: 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=800&q=80'
  },
  {
    id: 7,
    slug: 'motorway-driving-for-new-drivers',
    title: 'Motorway Driving Guide for New Drivers',
    excerpt: 'Everything you need to know about driving on UK motorways safely and confidently.',
    content: `New drivers can now take motorway lessons before passing their test. Here's what you need to know.

## Before Your First Motorway Trip

### Preparation:
- Take professional motorway lessons
- Build up your confidence on dual carriageways first
- Understand motorway rules thoroughly
- Check your vehicle is roadworthy

## Joining the Motorway

### Slip Road Technique:
1. Build up speed on the slip road
2. Match the speed of traffic in the left lane
3. Check mirrors and blind spot
4. Signal right
5. Merge when safe

## Lane Discipline

### Rules:
- Keep left unless overtaking
- Only use right lanes for overtaking
- Don't middle-lane hog
- Return to left lane after overtaking

## Speed and Following Distance

- Speed limit: 70mph (unless signs say otherwise)
- Maintain 2-second gap in good conditions
- Increase to 4 seconds in rain
- Use the 'only a fool breaks the two-second rule' method

## Overtaking Safely

### Steps:
1. Check mirrors
2. Signal right early
3. Check blind spot
4. Move out if safe
5. Pass smoothly
6. Signal left
7. Return to left lane

## What to Do If You Break Down

1. Move to hard shoulder if possible
2. Exit via left-hand door
3. Stand behind barrier
4. Call for help (emergency phone or mobile)
5. Return to car only when safe

## Smart Motorways

- Red X means lane closed - don't drive in it
- Variable speed limits are mandatory
- Emergency refuge areas are for emergencies only

## Common Mistakes to Avoid

- Driving too slowly
- Last-minute lane changes
- Not checking blind spots
- Undertaking
- Stopping on hard shoulder unnecessarily

Stay alert and drive confidently!`,
    category: 'Driving Skills',
    tags: ['motorway', 'new drivers', 'highway', 'safety'],
    author: 'TESTBOOK Team',
    date: '2024-12-20',
    image: 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=800&q=80'
  },
  {
    id: 8,
    slug: 'understanding-uk-road-signs',
    title: 'Complete Guide to UK Road Signs and Markings',
    excerpt: 'Master all UK road signs, signals, and markings to become a confident driver.',
    content: `Understanding road signs is essential for safe driving and passing your test.

## Types of Road Signs

### Warning Signs (Red Triangle)
- Alert you to hazards ahead
- Examples: sharp bends, school crossings, road works
- Action: Slow down and be prepared

### Regulatory Signs (Circular)

#### Red Circle = Prohibitive
- No entry
- Speed limits
- No stopping

#### Blue Circle = Mandatory
- Turn left/right
- Minimum speed
- Cycle lane

### Information Signs (Rectangular)
- Blue: Motorway information
- Green: Primary routes
- White: Local routes
- Brown: Tourist attractions

## Road Markings

### White Lines

#### Single Broken Line
- Lane divider
- Can cross if safe

#### Double White Lines
- Solid on your side: Do not cross
- Broken on your side: Can cross if safe

#### Zig-Zag Lines
- No stopping or parking
- Usually at pedestrian crossings

### Yellow Lines

Indicate parking restrictions:
- Single yellow: Some restrictions
- Double yellow: No parking at any time
- Red lines: Strictest restrictions

## Pedestrian Crossings

### Types:
1. Zebra Crossing - Black and white stripes, flashing beacons
2. Pelican Crossing - Controlled by lights, flashing amber phase
3. Puffin Crossing - Sensors detect pedestrians
4. Toucan Crossing - For pedestrians and cyclists
5. Pegasus Crossing - For horse riders

## Traffic Lights

### Sequence:
- Red: Stop
- Red and Amber: Prepare to go
- Green: Go if safe
- Amber: Stop unless unsafe to do so
- Flashing Amber: At pelican crossings, give way to pedestrians

## Box Junctions

Yellow criss-cross:
- Don't enter unless exit is clear
- Exception: Can wait to turn right

## Motorway Signs

- Red X: Lane closed
- Speed limit signs: Mandatory
- Countdown markers: Exit approaching
- Information signs: Services, distances

Study these regularly - they could save your life!`,
    category: 'Highway Code',
    tags: ['road signs', 'highway code', 'markings', 'regulations'],
    author: 'TESTBOOK Team',
    date: '2024-12-15',
    image: 'https://images.unsplash.com/photo-1533561797500-4ffd1ec148b2?w=800&q=80'
  },
  {
    id: 9,
    slug: 'eco-driving-tips-fuel-efficiency',
    title: 'Eco-Driving: Save Fuel and Reduce Your Carbon Footprint',
    excerpt: 'Learn eco-friendly driving techniques that save money and protect the environment.',
    content: `Eco-driving helps you save fuel, reduce emissions, and drive more safely.

## What is Eco-Driving?

Eco-driving is a smooth, efficient driving style that:
- Reduces fuel consumption
- Lowers CO2 emissions
- Reduces wear and tear
- Improves road safety

## Top Eco-Driving Techniques

### 1. Smooth Acceleration
- Accelerate gently
- Use higher gears as soon as possible
- Aim for 2,000-2,500 rpm for petrol
- Aim for 1,500-2,000 rpm for diesel

### 2. Maintain Steady Speed
- Avoid unnecessary acceleration and braking
- Use cruise control on motorways
- Anticipate traffic flow
- Read the road ahead

### 3. Reduce Drag
- Remove roof racks when not in use
- Close windows at high speeds
- Keep car aerodynamic
- Remove unnecessary weight

### 4. Check Tyre Pressure
- Under-inflated tyres increase fuel consumption
- Check pressure monthly
- Use recommended pressure levels

### 5. Turn Off Engine
- Switch off for waits over 30 seconds
- Many modern cars have stop-start technology
- Don't idle unnecessarily

### 6. Plan Your Journey
- Combine multiple errands
- Avoid peak traffic times
- Use sat nav efficiently
- Check traffic before setting off

### 7. Reduce Air Con Use
- Air conditioning increases fuel consumption by up to 10%
- Use it only when necessary
- Open windows at low speeds instead

### 8. Service Regularly
- Well-maintained engines are more efficient
- Change oil on schedule
- Replace air filters
- Keep engine tuned

## Benefits of Eco-Driving

### Financial:
- Save up to 25% on fuel costs
- Reduce maintenance expenses
- Lower insurance premiums (safer driving)

### Environmental:
- Reduce CO2 emissions by up to 10%
- Lower air pollution
- Contribute to cleaner air

### Safety:
- More anticipation = fewer accidents
- Calmer, less stressed driving
- Better for passengers

## Fuel Efficiency by Vehicle Type

- Petrol: Average 35-40 mpg
- Diesel: Average 45-50 mpg
- Hybrid: Average 50-60 mpg
- Electric: Zero fuel consumption

Small changes make a big difference!`,
    category: 'Driving Skills',
    tags: ['eco-driving', 'fuel efficiency', 'environment', 'savings'],
    author: 'TESTBOOK Team',
    date: '2024-12-10',
    image: 'https://images.unsplash.com/photo-1542601098-3adb3b8b5285?w=800&q=80'
  },
  {
    id: 10,
    slug: 'first-car-insurance-tips-new-drivers',
    title: 'First Car Insurance: Essential Guide for New Drivers',
    excerpt: 'Navigate the world of car insurance and find the best deals for new drivers.',
    content: `Getting your first car insurance can be expensive. Here's how to find affordable coverage.

## Understanding Insurance Types

### Third Party Only
- Cheapest option
- Covers damage to others only
- You pay for your own repairs
- Good for old, low-value cars

### Third Party, Fire and Theft
- Mid-range option
- Covers third parties plus fire/theft
- Still don't cover your own accident damage

### Comprehensive
- Most expensive
- Covers everything
- Often best value
- Recommended for new cars

## Factors Affecting Your Premium

### Age and Experience
- Under 25s pay more
- First year is most expensive
- Builds down with no-claims discount

### Vehicle Choice
- Insurance group (1-50)
- Engine size
- Value
- Security features

### Location
- Postcode affects price significantly
- Urban areas usually more expensive
- Secure parking helps

### Annual Mileage
- Lower mileage = lower premium
- Be honest about mileage
- Consider mileage restrictions

## Ways to Reduce Your Premium

### 1. Add Experienced Driver
- Parents as named drivers can reduce cost
- Don't make them main driver (fronting is illegal)

### 2. Black Box Insurance
- Telematics monitors your driving
- Can save up to 30%
- Rewards safe driving
- Perfect for new drivers

### 3. Pay Annually
- Monthly payments include interest
- Save up to 20% paying upfront

### 4. Increase Voluntary Excess
- Higher excess = lower premium
- Make sure you can afford it
- Balance risk vs reward

### 5. Choose Your Car Wisely
- Low insurance group (1-10)
- Smaller engine
- Common models are cheaper
- Avoid modifications

### 6. Build No-Claims Discount
- Each claim-free year reduces premium
- Can save up to 75% after 5 years
- Protect your NCB

### 7. Pass Your Test First Time
- Driving without license is illegal
- Insurance cheaper with full license

### 8. Advanced Driving Courses
- Pass Plus course
- IAM RoadSmart
- Can reduce premiums by 10-30%

## What to Look For

- Adequate coverage
- Breakdown cover included?
- Legal expenses cover
- Courtesy car provision
- Windscreen cover
- Personal belongings cover

## Common Mistakes to Avoid

### 1. Fronting
- Illegal
- Makes named driver appear as main driver
- Can void insurance

### 2. Incorrect Information
- Always tell the truth
- Update changes promptly
- Could invalidate claims

### 3. Auto-Renewal Without Shopping
- Loyalty doesn't pay
- Shop around every year
- Compare at least 10 quotes

### 4. Not Reading Terms
- Understand what's covered
- Know your excesses
- Check exclusions

## When to Buy

- 3-4 weeks before renewal
- Prices often lowest 21 days before
- Don't leave it to last day

## Top Comparison Sites

- Compare the Market
- Go Compare
- Confused.com
- Money Supermarket

## Average Costs (2025)

- 17-19 years: £1,500-2,500
- 20-24 years: £1,000-1,800
- 25+ years: £600-1,200

Patience pays off - prices drop as you gain experience!`,
    category: 'Insurance & Legal',
    tags: ['insurance', 'new drivers', 'first car', 'savings'],
    author: 'TESTBOOK Team',
    date: '2024-12-05',
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80'
  }
];

export const categories = ['All', 'Test Preparation', 'News & Updates', 'Learning to Drive', 'Driving Skills', 'Highway Code', 'Insurance & Legal'];

export const allTags = [
  'driving test',
  'preparation',
  'tips',
  'UK driving',
  'mistakes',
  'common errors',
  '2025',
  'changes',
  'DVSA',
  'updates',
  'driving instructor',
  'learning',
  'test centre',
  'what to expect',
  'weather',
  'safety',
  'driving conditions',
  'motorway',
  'new drivers',
  'highway',
  'road signs',
  'highway code',
  'markings',
  'regulations',
  'eco-driving',
  'fuel efficiency',
  'environment',
  'savings',
  'insurance',
  'first car'
];